"use client";

import type { VideoContainer } from "mediatools";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { ClipTimeline } from "../_components/ClipTimeline";
import { OptionRadioGroup } from "../_components/OptionRadioGroup";
import { SubtitlePanel } from "../_components/SubtitlePanel";
import { TimeRangeSelector } from "../_components/TimeRangeSelector";
import { useUndoHistory } from "../_components/useUndoHistory";
import { useZoomView } from "../_components/useZoomView";
import type { OperationOptionsProps } from "./types";

/** Extra context zoomed to around a subtitle cue, in seconds. */
const CUE_ZOOM_PADDING_S = 10;

/** MP4/WebM container picker shared by the video conversion tools. */
export function VideoFormatRadio({
  value,
  onChange,
  disabled,
  label,
}: {
  value: VideoContainer;
  onChange: (format: VideoContainer) => void;
  disabled: boolean;
  label?: string;
}) {
  const t = useTranslations("mediaTools.options.videoFormat");
  const videoFormats = [
    { value: "mp4" as const, label: "MP4", hint: t("mp4Hint") },
    { value: "webm" as const, label: "WebM", hint: t("webmHint") },
  ];

  return (
    <OptionRadioGroup
      name="video-format"
      label={label ?? t("label")}
      value={value}
      onChange={onChange}
      options={videoFormats}
      disabled={disabled}
    />
  );
}

export interface TimeRange {
  start: number;
  end: number;
}

/**
 * Shared options form for trim operations (audio + video): a start/end range
 * over the source duration, wired to the preview's playhead so you can scrub
 * and capture in/out points while watching. Initializes the end to the full
 * duration once it's known, then leaves the user in control.
 */
export function TimeRangeOptionsForm({
  options,
  setOptions,
  ctx,
  disabled,
  getPreviewElement,
}: OperationOptionsProps<TimeRange>) {
  const t = useTranslations("mediaTools.options.timeRange");
  const { duration, previewUrl } = ctx;
  const [current, setCurrent] = useState(0);

  // Initialize the end to the full duration once it's known.
  useEffect(() => {
    if (duration > 0 && options.end === 0) {
      setOptions({ start: 0, end: duration });
    }
  }, [duration, options.end, setOptions]);

  // Mirror the preview's playhead so the user can set in/out points from it.
  useEffect(() => {
    const el = getPreviewElement?.();
    if (!el) return;
    const sync = () => setCurrent(el.currentTime);
    sync();
    el.addEventListener("timeupdate", sync);
    el.addEventListener("seeked", sync);
    return () => {
      el.removeEventListener("timeupdate", sync);
      el.removeEventListener("seeked", sync);
    };
  }, [getPreviewElement, previewUrl]);

  if (duration <= 0) {
    return <p className="text-sm text-foreground/60">{t("loadFile")}</p>;
  }

  const seek = (time: number) => {
    const el = getPreviewElement?.();
    if (el) {
      el.currentTime = time;
      setCurrent(time);
    }
  };

  return (
    <TimeRangeSelector
      duration={duration}
      start={options.start}
      end={options.end}
      current={current}
      onChange={(start, end) => setOptions({ start, end })}
      onSeek={seek}
      onUsePlayheadStart={() =>
        setOptions({ start: Math.min(current, options.end - 0.1), end: options.end })
      }
      onUsePlayheadEnd={() =>
        setOptions({ start: options.start, end: Math.max(current, options.start + 0.1) })
      }
      disabled={disabled}
    />
  );
}

/**
 * Rich clip editor for trim-video: a timeline with draggable in/out handles,
 * loop-on-play preview, and editable timecodes (see {@link ClipTimeline}).
 * Initializes the end to the full duration once it's known.
 */
export function ClipOptionsForm({
  options,
  setOptions,
  ctx,
  disabled,
  getPreviewElement,
  setCaptionPreview,
}: OperationOptionsProps<TimeRange>) {
  const t = useTranslations("mediaTools.options.clip");
  const { duration, previewUrl } = ctx;
  const [showSubs, setShowSubs] = useState(false);

  const setRange = useCallback(
    (start: number, end: number) => setOptions({ start, end }),
    [setOptions]
  );

  // Options stay the source of truth; the history hook records checkpoints and
  // replays them by writing back through setOptions.
  const apply = useCallback(
    (next: TimeRange) => setOptions({ start: next.start, end: next.end }),
    [setOptions]
  );
  const history = useUndoHistory<TimeRange>({ start: options.start, end: options.end }, apply);

  // The zoom window is owned here (not in ClipTimeline) so subtitle cue picks
  // can drive it. commitView reads the live selection through this ref.
  const selectionRef = useRef({ start: options.start, end: options.end });
  selectionRef.current = { start: options.start, end: options.end };
  const zoom = useZoomView({ duration, selectionRef, onChange: setRange, history });

  useEffect(() => {
    if (duration > 0 && options.end === 0) {
      // Full clip is the baseline state — nothing to undo past it.
      setOptions({ start: 0, end: duration });
      history.reset();
    }
  }, [duration, options.end, setOptions, history.reset]);

  // Set the clip from a subtitle cue (or span of cues): an undoable edit that
  // seeks to the new start and zooms to give context around the cue.
  const pickRange = useCallback(
    (rangeStart: number, rangeEnd: number) => {
      const s = Math.max(0, Math.min(rangeStart, duration));
      const e = Math.max(s + 0.05, Math.min(rangeEnd, duration));
      history.record();
      setOptions({ start: s, end: e });
      zoom.zoomTo(s - CUE_ZOOM_PADDING_S, e + CUE_ZOOM_PADDING_S);
      const el = getPreviewElement?.();
      if (el) el.currentTime = s;
    },
    [duration, history, setOptions, getPreviewElement, zoom.zoomTo]
  );

  if (duration <= 0) {
    return <p className="text-sm text-foreground/60">{t("loadVideo")}</p>;
  }

  return (
    <div className="space-y-5">
      <ClipTimeline
        getPreviewElement={getPreviewElement}
        previewUrl={previewUrl}
        duration={duration}
        start={options.start}
        end={options.end}
        onChange={setRange}
        history={history}
        zoom={zoom}
        file={ctx.file}
        disabled={disabled}
      />

      {/* Progressive disclosure: subtitles are optional, hidden until asked for. */}
      <div className="border-t pt-3">
        <button
          type="button"
          className="text-sm text-foreground/60 underline-offset-4 hover:text-foreground/90 hover:underline"
          onClick={() => setShowSubs((value) => !value)}
        >
          {showSubs ? t("hideSubtitles") : t("findWithSubtitles")}
        </button>
        {showSubs && (
          <div className="mt-3 max-w-3xl">
            <SubtitlePanel
              getPreviewElement={getPreviewElement}
              setCaptionPreview={setCaptionPreview}
              onPickRange={pickRange}
              file={ctx.file}
              start={options.start}
              end={options.end}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}
